import { useCallback, useEffect, useState } from "react";
import apiClient from "@/lib/apiClient";
import { MagnifyingGlass, DownloadSimple, Eye, CaretLeft, CaretRight, CaretDown, CaretUp } from "@phosphor-icons/react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "—");
const BAND = { healthy: "bg-emerald-50 text-emerald-700 border-emerald-200", watch: "bg-amber-50 text-amber-700 border-amber-200", at_risk: "bg-red-50 text-red-600 border-red-200" };
const selectCls = "h-9 px-2.5 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";
const COLS = [["company_name", "Company"], [null, "Contact"], [null, "Plan / Status"], ["health_score", "Health"], ["last_activity_at", "Last activity"], ["usage_pct", "Usage"], ["confirmed_spend_usd", "Spend"], [null, "Segment"], [null, "Next action"]];

export const buildQuery = (f) => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p.set(k, v); });
  return p.toString();
};

export function ContactCell({ row }) {
  const [email, setEmail] = useState(null);
  const reveal = () => apiClient.get(`/owner/health/customers/${row.company_id}/contact`).then((r) => setEmail(r.data.support_email)).catch(() => setEmail("Unavailable"));
  if (email) return <span className="text-xs text-slate-600" data-testid={`contact-email-${row.company_id}`}>{email}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
      {row.support_email_masked || "—"}
      {row.support_email_masked && (
        <button type="button" onClick={reveal} data-testid={`contact-reveal-${row.company_id}`} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5"><Eye size={12} /> Reveal</button>
      )}
    </span>
  );
}

function Factors({ row }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 py-2" data-testid={`factors-${row.company_id}`}>
      {row.factors.map((f) => (
        <div key={f.key} className="bg-white border border-slate-200 rounded px-3 py-2">
          <div className="flex justify-between text-xs"><span className="font-semibold text-[#0F172A]">{f.label}</span><span className="text-slate-500">{f.points}/{f.max}</span></div>
          <div className="text-[11px] text-slate-500 mt-0.5">{f.detail}</div>
        </div>
      ))}
      {row.secondary?.length > 0 && <div className="text-[11px] text-slate-500 sm:col-span-2 lg:col-span-3">Also matches: {row.secondary.join(", ").replaceAll("_", " ")}</div>}
    </div>
  );
}

function Row({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`opp-row-${r.company_id}`}>
        <td className="py-2.5 px-2 font-semibold text-[#0F172A]">{r.company_name}{r.is_demo && <span className="ml-1.5 font-mono-plex text-[9px] uppercase px-1 rounded bg-slate-100 text-slate-500">demo</span>}</td>
        <td className="py-2.5 px-2"><ContactCell row={r} /></td>
        <td className="py-2.5 px-2 capitalize text-slate-600 text-xs">{r.plan}<div className="text-slate-400">{r.status}</div></td>
        <td className="py-2.5 px-2">
          <button type="button" onClick={() => setOpen(!open)} data-testid={`opp-score-${r.company_id}`} className={`font-mono-plex text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${BAND[r.health_band]}`}>
            {r.health_score} {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
          </button>
        </td>
        <td className="py-2.5 px-2 text-slate-500 text-xs">{shortDate(r.last_activity_at)}</td>
        <td className="py-2.5 px-2 text-slate-600 text-xs">{r.usage_limit ? `${r.usage_used}/${r.usage_limit}` : `${r.usage_used} / ∞`}</td>
        <td className="py-2.5 px-2 font-semibold text-[#0F172A] text-xs">{money(r.confirmed_spend_usd)}</td>
        <td className="py-2.5 px-2 text-xs"><span className="font-semibold text-[#0F172A]" data-testid={`opp-segment-${r.company_id}`}>{r.segment_label}</span><div className="text-slate-400">{r.reason}</div></td>
        <td className="py-2.5 px-2 text-xs text-slate-600 max-w-[220px]">{r.recommended_action}</td>
      </tr>
      {open && <tr className="bg-slate-50"><td colSpan={9} className="px-2"><Factors row={r} /></td></tr>}
    </>
  );
}

function Filters({ f, set, meta, segments, onExport }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="opp-filters">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="opp-search" value={f.search} onChange={(e) => set({ search: e.target.value })} placeholder="Search company or email" className={`${selectCls} pl-8 w-56`} />
      </div>
      <select data-testid="opp-filter-segment" value={f.segment} onChange={(e) => set({ segment: e.target.value })} className={selectCls}>
        <option value="">All segments</option>{segments.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
      <select data-testid="opp-filter-plan" value={f.plan} onChange={(e) => set({ plan: e.target.value })} className={selectCls}>
        <option value="">All plans</option>{meta.plans.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select data-testid="opp-filter-status" value={f.status} onChange={(e) => set({ status: e.target.value })} className={selectCls}>
        <option value="">All statuses</option>{meta.statuses.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select data-testid="opp-filter-activity" value={f.activity} onChange={(e) => set({ activity: e.target.value })} className={selectCls}>
        <option value="">Any activity</option><option value="7">Active 7d</option><option value="30">Active 30d</option><option value="90">Active 90d</option><option value="inactive_30">Inactive 30d+</option>
      </select>
      <button type="button" onClick={onExport} data-testid="opp-export-csv" className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-[#0F172A]">
        <DownloadSimple size={15} weight="bold" /> Export CSV
      </button>
    </div>
  );
}

export function OpportunitiesTable({ segments, segment, onSegment }) {
  const [f, setF] = useState({ search: "", plan: "", status: "", activity: "", sort: "health_score", order: "asc", page: 1 });
  const [data, setData] = useState({ rows: [], total: 0, plans: [], statuses: [], page_size: 25 });
  const set = (patch) => setF((p) => ({ ...p, ...patch, page: patch.page || 1 }));
  const filters = { ...f, segment };

  useEffect(() => { setF((p) => ({ ...p, page: 1 })); }, [segment]);

  const load = useCallback(() => {
    apiClient.get(`/owner/health/customers?${buildQuery({ ...filters, page_size: 25 })}`).then((r) => setData(r.data)).catch(() => {});
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    const { page, ...rest } = filters;
    const r = await apiClient.get(`/owner/health/export.csv?${buildQuery(rest)}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "customer-health.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const sortBy = (k) => k && set({ sort: k, order: f.sort === k && f.order === "asc" ? "desc" : "asc" });
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  useEffect(() => { if (f.page > pages) setF((p) => ({ ...p, page: pages })); }, [f.page, pages]);

  return (
    <div className="space-y-4" data-testid="opportunities-table">
      <Filters f={filters} set={(p) => ("segment" in p ? onSegment(p.segment) : set(p))} meta={data} segments={segments} onExport={exportCsv} />
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="text-left font-mono-plex text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-slate-200">
              {COLS.map(([k, label]) => (
                <th key={label} className="py-2.5 px-2">
                  {k ? <button type="button" onClick={() => sortBy(k)} data-testid={`opp-sort-${k}`} className="uppercase hover:text-[#0F172A]">{label}{f.sort === k ? (f.order === "asc" ? " ↑" : " ↓") : ""}</button> : label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => <Row key={r.company_id} r={r} />)}
            {data.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400" data-testid="opp-empty">No customers match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500" data-testid="opp-pagination">
        <span data-testid="opp-total">{data.total} customers</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={f.page <= 1} onClick={() => set({ page: f.page - 1 })} data-testid="opp-prev" className="h-8 w-8 rounded border border-slate-200 disabled:opacity-40 inline-flex items-center justify-center"><CaretLeft size={14} /></button>
          <span data-testid="opp-page">Page {f.page} / {pages}</span>
          <button type="button" disabled={f.page >= pages} onClick={() => set({ page: f.page + 1 })} data-testid="opp-next" className="h-8 w-8 rounded border border-slate-200 disabled:opacity-40 inline-flex items-center justify-center"><CaretRight size={14} /></button>
        </div>
      </div>
    </div>
  );
}
